import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, Square, Play, Pause, RotateCcw, Check, X, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface VideoRecorderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVideoReady: (videoUrl: string) => void;
  maxDurationSeconds?: number;
}

export function VideoRecorder({
  open,
  onOpenChange,
  onVideoReady,
  maxDurationSeconds = 300, // 5 minutes default
}: VideoRecorderProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isInitializing, setIsInitializing] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resolveRecordingMimeType = useCallback(() => {
    const preferred = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    ];

    return preferred.find((type) => MediaRecorder.isTypeSupported(type)) || '';
  }, []);

  const extensionFromMimeType = useCallback((mimeType: string) => {
    if (mimeType.includes('mp4')) return 'mp4';
    if (mimeType.includes('quicktime')) return 'mov';
    return 'webm';
  }, []);

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Initialize camera when dialog opens
  useEffect(() => {
    if (open && !cameraReady) {
      initCamera();
    }

    return () => {
      if (!open) {
        cleanup();
      }
    };
  }, [open]);

  const initCamera = async () => {
    setIsInitializing(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch (error) {
      console.error('Camera access error:', error);
      toast.error('Could not access camera. Please check permissions.');
      onOpenChange(false);
    } finally {
      setIsInitializing(false);
    }
  };

  const cleanup = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setCameraReady(false);
    setIsRecording(false);
    setIsPaused(false);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsedTime(0);
    chunksRef.current = [];
  };

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;

    chunksRef.current = [];
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsedTime(0);

    const mimeType = resolveRecordingMimeType();
    const mediaRecorder = new MediaRecorder(
      streamRef.current,
      mimeType ? { mimeType } : undefined
    );

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const finalMimeType = mediaRecorder.mimeType || chunksRef.current[0]?.type || 'video/webm';
      const blob = new Blob(chunksRef.current, { type: finalMimeType });
      console.log('[VideoRecorder] video_blob_created', { size: blob.size, type: blob.type });
      setRecordedBlob(blob);
      const url = URL.createObjectURL(blob);
      setRecordedUrl(url);

      // Switch video element from live camera to recorded playback
      if (videoRef.current) {
        videoRef.current.srcObject = null; // Clear live stream so src takes effect
        videoRef.current.src = url;
        videoRef.current.muted = false;
        videoRef.current.load(); // Force metadata load for duration
        console.log('[VideoRecorder] preview_mode_entered');
      }
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(1000);
    setIsRecording(true);
    setIsPaused(false);

    // Start timer
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => {
        if (prev >= maxDurationSeconds - 1) {
          stopRecording();
          return maxDurationSeconds;
        }
        return prev + 1;
      });
    }, 1000);
  }, [maxDurationSeconds, resolveRecordingMimeType]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setElapsedTime(prev => {
            if (prev >= maxDurationSeconds - 1) {
              stopRecording();
              return maxDurationSeconds;
            }
            return prev + 1;
          });
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
      setIsPaused(!isPaused);
    }
  }, [isRecording, isPaused, maxDurationSeconds, stopRecording]);

  const retryRecording = useCallback(() => {
    console.log('[VideoRecorder] record_again_clicked');
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsedTime(0);
    chunksRef.current = [];
    
    // Switch back to live camera preview
    if (videoRef.current && streamRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute('src');
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, [recordedUrl]);

  const handleConfirm = async () => {
    if (!recordedBlob) return;

    setIsUploading(true);
    try {
      // Upload video to Supabase storage
      const { data: sessionData } = await (supabase.auth as any).getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) {
        toast.error('Please sign in to save video.');
        setIsUploading(false);
        return;
      }

      const mimeType = recordedBlob.type || 'video/webm';
      const maxUploadSize = 157286400; // 150 MB (matches bucket limit)
      if (recordedBlob.size > maxUploadSize) {
        toast.error('Video is too large. Please record a shorter message.');
        setIsUploading(false);
        return;
      }

      const fileName = `${userId}/${Date.now()}-video.${extensionFromMimeType(mimeType)}`;
      const { error: uploadError } = await supabase.storage
        .from('announcement-photos')
        .upload(fileName, recordedBlob, { contentType: mimeType });

      if (uploadError) {
        console.error('Video upload error:', uploadError);
        const details = uploadError.message?.toLowerCase() || '';
        if (details.includes('mime') || details.includes('size')) {
          toast.error('This video format or size is not supported. Please try a shorter recording.');
        } else {
          toast.error('Failed to upload video. Please try again.');
        }
        setIsUploading(false);
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('announcement-photos')
        .getPublicUrl(fileName);

      onVideoReady(publicUrl);
      toast.success('Video message recorded successfully!');
      onOpenChange(false);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to save video. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    cleanup();
    onOpenChange(false);
  };

  const progressPercent = (elapsedTime / maxDurationSeconds) * 100;
  const remainingTime = maxDurationSeconds - elapsedTime;

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" />
            Record Video Message
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Video Preview */}
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            {isInitializing && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
            
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              controls={!!recordedUrl}
            />

            {/* Recording indicator */}
            {isRecording && (
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <span className={cn(
                  "w-3 h-3 rounded-full",
                  isPaused ? "bg-yellow-500" : "bg-red-500 animate-pulse"
                )} />
                <span className="text-white text-sm font-medium drop-shadow-lg">
                  {formatTime(elapsedTime)}
                </span>
              </div>
            )}

            {/* Time remaining */}
            {isRecording && (
              <div className="absolute top-4 right-4">
                <span className="text-white text-xs bg-black/50 px-2 py-1 rounded-full">
                  {formatTime(remainingTime)} remaining
                </span>
              </div>
            )}
          </div>

          {/* Progress bar during recording */}
          {isRecording && (
            <div className="space-y-1">
              <Progress value={progressPercent} className="h-2" />
              <p className="text-xs text-muted-foreground text-center">
                Maximum {Math.floor(maxDurationSeconds / 60)} minutes
              </p>
            </div>
          )}

          {/* Controls */}
          <div className="flex items-center justify-center gap-3">
            {!recordedUrl ? (
              <>
                {!isRecording ? (
                  <Button
                    onClick={startRecording}
                    disabled={!cameraReady || isInitializing}
                    className="gap-2"
                    size="lg"
                  >
                    <div className="w-4 h-4 rounded-full bg-red-500" />
                    Start Recording
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={pauseRecording}
                      variant="outline"
                      size="icon"
                      className="h-12 w-12"
                    >
                      {isPaused ? (
                        <Play className="h-5 w-5" />
                      ) : (
                        <Pause className="h-5 w-5" />
                      )}
                    </Button>
                    <Button
                      onClick={stopRecording}
                      variant="destructive"
                      size="icon"
                      className="h-14 w-14"
                    >
                      <Square className="h-6 w-6" />
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <Button
                  onClick={retryRecording}
                  variant="outline"
                  className="gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Record Again
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={isUploading}
                  className="gap-2"
                >
                  {isUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  Use This Video
                </Button>
              </>
            )}
          </div>

          {/* Tips */}
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground text-center">
              💡 Find a quiet spot with good lighting. Speak from the heart — 2-5 minutes is ideal.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
